import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { AdminUser, PlanTier } from '../types'

type BillingCycle = 'monthly' | 'annual'

async function invoke(action: string, extra?: Record<string, unknown>) {
  const { data: { session } } = await supabase.auth.getSession()
  const res = await supabase.functions.invoke('admin-manage-users', {
    body: { action, ...extra },
    headers: { Authorization: `Bearer ${session?.access_token}` },
  })
  if (res.error) throw new Error((res.data as Record<string, unknown>)?.error as string || res.error.message)
  return res.data as Record<string, unknown>
}

export function useAdminPanel() {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true)
    setError(null)
    try {
      const data = await invoke('list-users', { page })
      setUsers(data.users as AdminUser[])
      setTotal((data.total as number) ?? (data.users as AdminUser[]).length)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro desconhecido')
    } finally {
      setLoading(false)
    }
  }, [])

  const searchUsers = useCallback(async (q: string) => {
    if (!q.trim()) {
      fetchUsers(1)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await invoke('search-users', { q })
      setUsers(data.users as AdminUser[])
      setTotal((data.users as AdminUser[]).length)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro desconhecido')
    } finally {
      setLoading(false)
    }
  }, [fetchUsers])

  const setPlan = useCallback(async (
    targetUserId: string,
    planTier: Exclude<PlanTier, 'free'>,
    billingCycle: BillingCycle,
    currentPeriodEnd: string | null,
  ) => {
    const data = await invoke('set-plan', { targetUserId, planTier, billingCycle, currentPeriodEnd })
    setUsers((prev) =>
      prev.map((u) =>
        u.id === targetUserId
          ? { ...u, plan_tier: planTier, sub_status: 'authorized', current_period_end: currentPeriodEnd }
          : u
      )
    )
    return data.subscription
  }, [])

  const revokePlan = useCallback(async (targetUserId: string) => {
    await invoke('revoke-plan', { targetUserId })
    setUsers((prev) =>
      prev.map((u) =>
        u.id === targetUserId
          ? { ...u, plan_tier: 'free', sub_status: null, current_period_end: null }
          : u
      )
    )
  }, [])

  return {
    users,
    total,
    loading,
    error,
    fetchUsers,
    searchUsers,
    setPlan,
    revokePlan,
  }
}
